/* eslint-disable */

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Pencil, Archive, ArchiveRestore, Trash2, Briefcase, Search, Loader2, ExternalLink, FileText, AlertCircle, Send, CheckCircle2 } from "lucide-react";
import { useConfirm } from "../components/ConfirmDialog";
import { toast } from "sonner";

function daysAgo(iso) {
  if (!iso) return null;
  try {
    return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  } catch { return null; }
}

const emptyForm = {
  name: "",
  legal_name: "",
  contact_name: "",
  email: "",
  billing_email: "",
  phone: "",
  address: "",
  postal_code: "",
  city: "",
  notes: "",
  displayed_product_ids: [],
  price_overrides: {},
  discount_percent: "",
};

export default function Resellers() {
  const [resellers, setResellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [costSummary, setCostSummary] = useState({}); // Phase 98ag — {product_id: {total_unit_cost, ingredient_cost, packaging_cost}}
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [productQuery, setProductQuery] = useState("");
  // Phase 101ew — Filtre activation
  const [activationFilter, setActivationFilter] = useState("all"); // "all" | "not_activated" | "activated" | "no_invite"
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const [r, p, s, c] = await Promise.all([
        api.get(`/resellers${showArchived ? "?include_archived=true" : ""}`),
        api.get("/products"),
        api.get("/resellers-summary").catch(() => ({ data: null })),
        api.get("/products-cost-summary").catch(() => ({ data: {} })),
      ]);
      setResellers(r.data || []);
      setProducts((p.data || []).filter((x) => !x.archived).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setSummary(s.data);
      setCostSummary(c.data || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = resellers;
    // Phase 101ew — Filtres activation
    if (activationFilter === "not_activated") {
      // Lien envoyé mais pas encore cliqué
      base = base.filter((r) => r.activation_token && !r.activated_at);
    } else if (activationFilter === "activated") {
      base = base.filter((r) => r.activated_at);
    } else if (activationFilter === "no_invite") {
      // Jamais invité (ni token, ni activation)
      base = base.filter((r) => !r.activation_token && !r.activated_at);
    }
    if (!q) return base;
    return base.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.legal_name || "").toLowerCase().includes(q) ||
      (r.city || "").toLowerCase().includes(q) ||
      (r.contact_name || "").toLowerCase().includes(q)
    );
  }, [resellers, query, activationFilter]);

  // Phase 101ew — Stats activation
  const activationStats = useMemo(() => ({
    total: resellers.length,
    activated: resellers.filter((r) => r.activated_at).length,
    pending: resellers.filter((r) => r.activation_token && !r.activated_at).length,
    no_invite: resellers.filter((r) => !r.activation_token && !r.activated_at).length,
  }), [resellers]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setProductQuery("");
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || "",
      legal_name: r.legal_name || "",
      contact_name: r.contact_name || "",
      email: r.email || "",
      billing_email: r.billing_email || "",
      phone: r.phone || "",
      address: r.address || "",
      postal_code: r.postal_code || "",
      city: r.city || "",
      notes: r.notes || "",
      displayed_product_ids: r.displayed_product_ids || [],
      price_overrides: { ...(r.price_overrides || {}) },
      discount_percent: r.discount_percent != null ? String(r.discount_percent) : "",
    });
    setProductQuery("");
    setOpen(true);
  };

  const toggleProduct = (pid) => {
    setForm((f) => {
      const has = f.displayed_product_ids.includes(pid);
      const next = has
        ? f.displayed_product_ids.filter((id) => id !== pid)
        : [...f.displayed_product_ids, pid];
      const overrides = { ...f.price_overrides };
      if (has) delete overrides[pid];
      return { ...f, displayed_product_ids: next, price_overrides: overrides };
    });
  };

  const setOverride = (pid, value) => {
    setForm((f) => {
      const overrides = { ...f.price_overrides };
      if (value === "" || value == null) {
        delete overrides[pid];
      } else {
        const n = Number(value);
        if (!Number.isNaN(n) && n >= 0) overrides[pid] = n;
      }
      return { ...f, price_overrides: overrides };
    });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Le nom est obligatoire");
      return;
    }
    const dp = form.discount_percent === "" ? null : Number(form.discount_percent);
    if (dp !== null && (Number.isNaN(dp) || dp < 0 || dp > 100)) {
      toast.error("Le rabais doit être un nombre entre 0 et 100");
      return;
    }
    const payload = {
      ...form,
      discount_percent: dp,
      // Nettoyage : pas de prix override pour un produit qui n'est pas dans displayed_product_ids
      price_overrides: Object.fromEntries(
        Object.entries(form.price_overrides).filter(([pid]) => form.displayed_product_ids.length === 0 || form.displayed_product_ids.includes(pid))
      ),
    };
    try {
      if (editing) {
        await api.put(`/resellers/${editing.id}`, payload);
        toast.success("Revendeur mis à jour");
      } else {
        await api.post("/resellers", payload);
        toast.success("Revendeur créé");
      }
      setOpen(false);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur";
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'enregistrement");
    }
  };

  const sendActivation = async (r) => {
    if (!r.email) {
      toast.error("Renseignez d'abord un email sur ce revendeur.");
      return;
    }
    const ok = await confirm({
      title: `Envoyer le lien d'activation à ${r.email} ?`,
      description: "Le revendeur recevra un email avec un lien magique valable 7 jours pour activer son compte boutique. Tout précédent lien sera invalidé.",
      confirmText: "Envoyer",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/resellers/${r.id}/send-activation-link`);
      if (data.email_sent) {
        toast.success(`Email envoyé à ${r.email}`);
      } else if (data.email_skipped) {
        toast.warning(`Token généré, mais email non envoyé (${data.email_error || "Resend non configuré"}). Communiquez le lien manuellement.`);
      } else {
        toast.warning(`Token généré, l'email a peut-être échoué.`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi du lien");
    }
  };

  const archive = async (r) => {
    const ok = await confirm({
      title: `Archiver ${r.name} ?`,
      description: "Le revendeur sera masqué de la liste active. Vous pourrez le restaurer plus tard depuis le filtre 'Archivés'.",
      confirmText: "Archiver",
      destructive: false,
    });
    if (!ok) return;
    await api.delete(`/resellers/${r.id}`);
    toast.success("Revendeur archivé");
    await load();
  };

  const restore = async (r) => {
    await api.put(`/resellers/${r.id}`, { archived: false });
    toast.success("Revendeur restauré");
    await load();
  };

  const hardDelete = async (r) => {
    const ok = await confirm({
      title: `Supprimer définitivement ${r.name} ?`,
      description: "Cette action est irréversible. Toutes les données du revendeur seront perdues.",
      confirmText: "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/resellers/${r.id}?hard=true`);
    toast.success("Revendeur supprimé");
    await load();
  };

  const productsForSelection = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q));
  }, [products, productQuery]);

  return (
    <div className="space-y-6" data-testid="resellers-page">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-heading flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-[#8C6B2F]" /> Revendeurs B2B
          </h2>
          <p className="text-sm text-[#575A57] mt-1 max-w-2xl">
            Gestion des partenaires revendeurs (Station Shop, OSEO, etc.) qui achètent vos barquettes à prix négocié pour les revendre.
            Chaque revendeur a son catalogue + ses prix override + un PDF tarif nominatif imprimable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/revendeurs/candidatures"
            className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[#E5E5E0] bg-white hover:bg-[#F4F4F0] text-sm text-[#575A57] hover:text-[#1F4E3D]"
            data-testid="open-applications-btn"
            title="Candidatures reçues depuis la landing publique"
          >
            <ExternalLink className="w-4 h-4" /> Candidatures
          </Link>
          <Button onClick={openCreate} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-reseller-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau revendeur
          </Button>
        </div>
      </header>

      {/* Stats globales */}
      {summary && summary.total > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="resellers-summary">
          <div className="bg-white border border-[#E5E5E0] rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold">Actifs</div>
            <div className="text-xl font-heading text-[#1F4E3D] mt-1">{summary.active}<span className="text-xs text-[#A3A6A3] ml-1">/ {summary.total}</span></div>
            <div className="text-[11px] text-[#A3A6A3]">activité récente</div>
          </div>
          <div className="bg-white border border-[#E5E5E0] rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold">Dormants</div>
            <div className={`text-xl font-heading mt-1 ${summary.dormant > 0 ? "text-[#8C6B2F]" : "text-[#A3A6A3]"}`}>{summary.dormant}</div>
            <div className="text-[11px] text-[#A3A6A3]">aucune activité depuis 90 j</div>
          </div>
          <div className="bg-white border border-[#E5E5E0] rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold">Catalogue moyen</div>
            <div className="text-xl font-heading text-[#3D2914] mt-1">{summary.avg_catalog_size}</div>
            <div className="text-[11px] text-[#A3A6A3]">plats par revendeur</div>
          </div>
          <div className="bg-white border border-[#E5E5E0] rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold">Overrides moyens</div>
            <div className="text-xl font-heading text-[#8C6B2F] mt-1">{summary.avg_overrides_count}</div>
            <div className="text-[11px] text-[#A3A6A3]">prix personnalisés / revendeur</div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#575A57]" />
          <Input
            placeholder="Rechercher un revendeur…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 bg-white"
            data-testid="reseller-search"
          />
        </div>
        {/* Phase 101ew — Filtre activation */}
        <div className="flex items-center gap-1 flex-wrap" data-testid="activation-filters">
          {[
            { v: "all", label: "Tous", count: activationStats.total, color: "bg-white text-[#3D2914] border-[#E5E5E0]" },
            { v: "not_activated", label: "En attente activation", count: activationStats.pending, color: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
            { v: "activated", label: "Activés", count: activationStats.activated, color: "bg-[#E8F2EC] text-[#1F4E3D] border-[#A8C9B6]" },
            { v: "no_invite", label: "Jamais invité", count: activationStats.no_invite, color: "bg-[#F5F5F0] text-[#575A57] border-[#E5E5E0]" },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setActivationFilter(opt.v)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                activationFilter === opt.v
                  ? "bg-[#1F4E3D] text-white border-[#1F4E3D]"
                  : opt.color + " hover:brightness-95"
              }`}
              data-testid={`filter-activation-${opt.v}`}
            >
              {opt.label} <span className="opacity-70">({opt.count})</span>
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer text-[#3D2914]">
          <Checkbox checked={showArchived} onCheckedChange={(v) => setShowArchived(!!v)} data-testid="toggle-archived" />
          Inclure les archivés
        </label>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Nom</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Contact</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Ville</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Catalogue</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Overrides</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Dernier PDF</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="resellers-table-body">
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline" /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-[#575A57] py-10 italic">Aucun revendeur. Cliquez sur « Nouveau revendeur » pour démarrer.</TableCell></TableRow>
            ) : filtered.map((r) => {
              const catalogCount = (r.displayed_product_ids || []).length;
              const overridesCount = Object.keys(r.price_overrides || {}).length;
              const pdfStats = summary?.pdf_stats_by_reseller?.[r.id];
              const lastPdfDays = daysAgo(pdfStats?.last_pdf_at);
              return (
                <TableRow key={r.id} data-testid={`reseller-row-${r.id}`} className={r.archived ? "opacity-50" : ""}>
                  <TableCell>
                    <Link to={`/revendeurs/${r.id}`} className="font-medium hover:text-[#1F4E3D] hover:underline" data-testid={`detail-link-${r.id}`}>
                      {r.name}
                    </Link>
                    {r.archived && <span className="ml-2 text-xs text-[#A3A6A3]">(archivé)</span>}
                    {r.legal_name && <div className="text-xs text-[#A3A6A3]">{r.legal_name}</div>}
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.contact_name && <div>{r.contact_name}</div>}
                    {r.email && <a href={`mailto:${r.email}`} className="text-xs text-[#1F4E3D] hover:underline">{r.email}</a>}
                    {r.phone && <div className="text-xs text-[#575A57]">{r.phone}</div>}
                    {r.email && (
                      r.activated_at ? (
                        <span
                          className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded bg-[#EAF3EE] text-[#1F4E3D] font-medium"
                          title={`Compte boutique activé le ${new Date(r.activated_at).toLocaleDateString("fr-CH")}`}
                          data-testid={`reseller-activated-${r.id}`}
                        >
                          <CheckCircle2 className="w-2.5 h-2.5" /> Compte activé
                        </span>
                      ) : r.activation_token ? (
                        <span className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded bg-[#FBF4E9] text-[#8C6B2F] font-medium" data-testid={`reseller-pending-${r.id}`}>
                          Lien envoyé · en attente
                        </span>
                      ) : null
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{r.postal_code} {r.city}</TableCell>
                  <TableCell className="text-right text-sm">
                    {catalogCount > 0 ? `${catalogCount} plat${catalogCount > 1 ? "s" : ""}` : <span className="text-[#A3A6A3] italic">Tous</span>}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {overridesCount > 0 ? <span className="text-[#8C6B2F] font-medium">{overridesCount}</span> : <span className="text-[#A3A6A3]">—</span>}
                    {r.discount_percent != null && (
                      <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#EAF3EE] text-[#1F4E3D]" title="Rabais global appliqué">
                        −{r.discount_percent}%
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs">
                    {pdfStats?.last_pdf_at ? (
                      <div>
                        <div className={lastPdfDays != null && lastPdfDays > 90 ? "text-[#8C6B2F]" : "text-[#3D2914]"}>
                          {lastPdfDays === 0 ? "aujourd'hui" : `il y a ${lastPdfDays} j`}
                        </div>
                        <div className="text-[10px] text-[#A3A6A3]">{pdfStats.pdf_total} total</div>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#A3A6A3] italic">
                        <AlertCircle className="w-3 h-3" /> Jamais
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Link to={`/revendeurs/${r.id}`}>
                        <Button variant="ghost" size="icon" title="Voir l'activité" data-testid={`activity-reseller-${r.id}`}>
                          <FileText className="w-4 h-4 text-[#1F4E3D]" />
                        </Button>
                      </Link>
                      {!r.archived && r.email && !r.activated_at && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => sendActivation(r)}
                          title={r.activation_token ? "Renvoyer le lien d'activation" : "Envoyer le lien d'activation par email"}
                          data-testid={`send-activation-${r.id}`}
                        >
                          <Send className={`w-4 h-4 ${r.activation_token ? "text-[#8C6B2F]" : "text-[#1F4E3D]"}`} />
                        </Button>
                      )}
                      {!r.archived && (
                        <Button variant="ghost" size="icon" onClick={() => openEdit(r)} title="Modifier" data-testid={`edit-reseller-${r.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {r.archived ? (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => restore(r)} title="Restaurer" data-testid={`restore-reseller-${r.id}`}>
                            <ArchiveRestore className="w-4 h-4 text-[#1F4E3D]" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => hardDelete(r)} title="Supprimer définitivement" data-testid={`hard-delete-reseller-${r.id}`}>
                            <Trash2 className="w-4 h-4 text-[#C05C47]" />
                          </Button>
                        </>
                      ) : (
                        <Button variant="ghost" size="icon" onClick={() => archive(r)} title="Archiver" data-testid={`archive-reseller-${r.id}`}>
                          <Archive className="w-4 h-4 text-[#8C6B2F]" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Dialog création/édition */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E5E5E0]">
            <DialogTitle className="font-heading">{editing ? "Modifier le revendeur" : "Nouveau revendeur"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4 px-6 overflow-y-auto flex-1">
            {/* Identité */}
            <section>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nom commercial <span className="text-[#C05C47]">*</span></Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Station Shop Chermignon" data-testid="reseller-name" />
                </div>
                <div>
                  <Label>Raison sociale (légale)</Label>
                  <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder="Station Shop Chermignon SA" />
                </div>
              </div>
            </section>

            {/* Contact */}
            <section>
              <h3 className="text-xs uppercase tracking-wider text-[#575A57] font-semibold mb-2">Contact</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Personne de contact</Label>
                  <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Jean Dupont" />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+41 27 …" />
                </div>
                <div className="col-span-2">
                  <Label>Email <span className="text-[10px] text-[#A3A6A3] normal-case">(utilisé pour l'auto-lien lors de l'inscription boutique)</span></Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="contact@…" />
                </div>
                <div className="col-span-2">
                  <Label>Email facturation <span className="text-[10px] text-[#A3A6A3] normal-case">(si différent — cas OSEO : compta séparée du manager. Par défaut, factures envoyées à l'email principal.)</span></Label>
                  <Input
                    type="email"
                    value={form.billing_email}
                    onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
                    placeholder="compta@…"
                    data-testid="reseller-billing-email"
                  />
                </div>
              </div>
            </section>

            {/* Tarification B2B (Phase 31) */}
            <section>
              <h3 className="text-xs uppercase tracking-wider text-[#575A57] font-semibold mb-2">Tarification B2B</h3>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <Label>Rabais global (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={form.discount_percent}
                    onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                    placeholder="ex: 15"
                    data-testid="reseller-discount-percent"
                  />
                </div>
                <p className="text-xs text-[#575A57] pb-2 leading-snug">
                  Appliqué sur <strong>tous</strong> les produits affichés.<br />
                  Les éventuels prix négociés individuellement (ci-dessous) sont prioritaires.
                </p>
              </div>
            </section>

            {/* Adresse */}
            <section>
              <h3 className="text-xs uppercase tracking-wider text-[#575A57] font-semibold mb-2">Adresse</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-3">
                  <Label>Rue</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Route du …" />
                </div>
                <div>
                  <Label>NPA</Label>
                  <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} placeholder="3963" />
                </div>
                <div className="col-span-2">
                  <Label>Ville</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Chermignon" />
                </div>
              </div>
            </section>

            {/* Catalogue */}
            <section>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <h3 className="text-xs uppercase tracking-wider text-[#575A57] font-semibold">Catalogue proposé</h3>
                <div className="text-xs text-[#575A57]">
                  {form.displayed_product_ids.length === 0
                    ? <span className="italic">Tous les plats éligibles ({products.length})</span>
                    : <span><strong className="text-[#8C6B2F]">{form.displayed_product_ids.length}</strong> sélectionné{form.displayed_product_ids.length > 1 ? "s" : ""}</span>}
                  <button onClick={() => setForm({ ...form, displayed_product_ids: [], price_overrides: {} })} className="ml-3 underline hover:text-[#1F4E3D]">Tout décocher (tous proposés)</button>
                  {" · "}
                  <button onClick={() => setForm({ ...form, displayed_product_ids: products.map((p) => p.id) })} className="underline hover:text-[#1F4E3D]">Tout cocher</button>
                </div>
              </div>
              {/* Phase 98ag — Bandeau d'aide sur le prix de revient (confidentiel) */}
              <div className="mb-2 p-2 bg-[#EAF3EE] border border-[#C6DFCC] rounded text-[11px] text-[#1F4E3D] flex items-start gap-1.5" data-testid="reseller-cost-info-banner">
                <span className="text-[13px] leading-none mt-0.5">🔒</span>
                <div className="leading-snug">
                  <strong>Aide à la décision — visible admin uniquement.</strong> Le « Revient » (ingrédients + emballage)
                  et la « marge fabricant » ci-dessous ne sont <strong>jamais</strong> exportés sur les tarifs PDF, les
                  factures ou visibles côté boutique revendeur. Ligne rouge = vente à perte.
                  Certains produits sans recette complète affichent « à compléter ».
                </div>
              </div>
              {Object.keys(form.price_overrides || {}).length > 0 && (
                <div className="mb-2 flex items-center justify-between gap-2 p-2 bg-[#FBF4E9] border border-[#E8D3A8] rounded text-xs">
                  <span className="text-[#8C6B2F]">
                    <strong>{Object.keys(form.price_overrides).length}</strong> prix négocié{Object.keys(form.price_overrides).length > 1 ? "s" : ""} actif{Object.keys(form.price_overrides).length > 1 ? "s" : ""}
                    {form.discount_percent !== "" && form.discount_percent !== null && (
                      <span className="text-[#575A57]"> — ils sont prioritaires sur le rabais global de {form.discount_percent}%</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm("Réinitialiser TOUS les prix négociés pour ce revendeur ? Le rabais global s'appliquera alors automatiquement (si défini), sinon le prix B2B brut sera appliqué.")) {
                        setForm({ ...form, price_overrides: {} });
                        toast.success("Prix négociés réinitialisés");
                      }
                    }}
                    className="text-[#C05C47] hover:text-[#8B3A2A] underline font-semibold"
                    data-testid="reseller-reset-overrides"
                  >
                    Réinitialiser tous les prix négociés
                  </button>
                </div>
              )}
              {form.discount_percent !== "" && form.discount_percent !== null && (() => {
                const missingWholesale = productsForSelection.filter((p) => {
                  const checked = form.displayed_product_ids.includes(p.id);
                  const shown = checked || form.displayed_product_ids.length === 0;
                  return shown && p.price_distributor == null;
                });
                if (missingWholesale.length === 0) return null;
                return (
                  <div className="mb-2 p-2 bg-[#FBEEEA] border border-[#E8C1B4] rounded text-xs text-[#8B3A2A]">
                    <strong>⚠ {missingWholesale.length} produit{missingWholesale.length > 1 ? "s" : ""}</strong> sans prix revendeur saisi —
                    le rabais global de {form.discount_percent}% <strong>ne s&apos;appliquera PAS</strong> sur ces produits
                    (il n&apos;y a rien à réduire). Saisissez leur <em>price_distributor</em> dans Produits →
                    Édition, ou définissez un prix négocié individuel ci-dessous.
                  </div>
                );
              })()}
              <Input
                placeholder="Filtrer les plats par nom ou SKU…"
                value={productQuery}
                onChange={(e) => setProductQuery(e.target.value)}
                className="mb-2 text-sm"
              />
              <div className="border border-[#E5E5E0] rounded max-h-64 overflow-y-auto p-2 space-y-1">
                {productsForSelection.map((p) => {
                  const checked = form.displayed_product_ids.includes(p.id);
                  // Pour les overrides : on permet l'override même si "tous proposés"
                  const showOverride = checked || form.displayed_product_ids.length === 0;
                  const currentOverride = form.price_overrides[p.id];
                  const hasOverride = currentOverride != null && currentOverride !== "";
                  const wholesale = p.price_distributor;
                  const recommended = p.price_direct;
                  const marginChf = (wholesale != null && recommended != null)
                    ? (Number(recommended) - Number(wholesale))
                    : null;
                  // Prix qui s'appliquera si on supprime l'override (= prix B2B après remise % éventuelle)
                  const discountPct = form.discount_percent === "" || form.discount_percent == null
                    ? null : Number(form.discount_percent);
                  const fallbackPrice = (wholesale != null && !Number.isNaN(Number(wholesale)))
                    ? (discountPct != null && !Number.isNaN(discountPct)
                        ? Number(wholesale) * (1 - discountPct / 100)
                        : Number(wholesale))
                    : null;
                  // Phase 98ag — Prix de revient (fabrication) — visible ADMIN uniquement
                  const costInfo = costSummary[p.id] || {};
                  const costPrice = costInfo.total_unit_cost != null ? Number(costInfo.total_unit_cost) : null;
                  // Prix effectivement appliqué au revendeur (override > fallback wholesale-discount)
                  const effectivePrice = hasOverride
                    ? Number(currentOverride)
                    : fallbackPrice;
                  // Marge fabricant nette (ce qu'on gagne en vendant au revendeur)
                  const fabricantMarginChf = (costPrice != null && effectivePrice != null)
                    ? (effectivePrice - costPrice)
                    : null;
                  const fabricantMarginPct = (costPrice != null && effectivePrice != null && effectivePrice > 0)
                    ? ((effectivePrice - costPrice) / effectivePrice * 100)
                    : null;
                  const soldAtLoss = fabricantMarginChf != null && fabricantMarginChf < 0;
                  return (
                    <div
                      key={p.id}
                      className={`flex items-center gap-2 p-2 rounded text-xs ${
                        soldAtLoss ? "bg-[#FCEEEA] border border-[#E8C1B4]"
                        : hasOverride ? "bg-[#FBF4E9] border border-[#E8D3A8]"
                        : checked ? "bg-[#F4F4F0]"
                        : "hover:bg-[#FAFAF8]"
                      }`}
                      data-testid={`reseller-product-row-${p.sku}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggleProduct(p.id)} data-testid={`prod-check-${p.sku}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.name}</div>
                        <div className="text-[10px] text-[#A3A6A3] font-mono flex flex-wrap gap-x-2">
                          <span>{p.sku}</span>
                          <span>·</span>
                          {/* Phase 98ag — Prix de revient (interne, invisible pour revendeur) */}
                          <span
                            className={costPrice != null ? "text-[#8B3A2A]" : "text-[#A3A6A3]"}
                            title="Prix de revient interne (ingrédients + emballage) — jamais affiché sur PDF/tarif revendeur"
                            data-testid={`cost-price-${p.sku}`}
                          >
                            🔒 Revient : <span className="font-semibold">
                              {costPrice != null ? costPrice.toFixed(2) + " CHF" : "à compléter"}
                            </span>
                          </span>
                          <span>·</span>
                          <span>
                            Prix revendeur : <span className={wholesale == null ? "text-[#C05C47] font-semibold" : "text-[#8C6B2F] font-semibold"}>
                              {wholesale != null ? Number(wholesale).toFixed(2) + " CHF" : "non saisi ⚠"}
                            </span>
                          </span>
                          {discountPct != null && fallbackPrice != null && Math.abs(fallbackPrice - Number(wholesale)) > 0.005 && (
                            <>
                              <span>·</span>
                              <span className="text-[#1F4E3D]" title="Prix après application du rabais global">
                                après −{discountPct}% : <span className="font-semibold">{fallbackPrice.toFixed(2)} CHF</span>
                              </span>
                            </>
                          )}
                          {discountPct != null && wholesale == null && (
                            <>
                              <span>·</span>
                              <span className="text-[#C05C47]" title="Le rabais % ne peut pas s'appliquer sans prix B2B saisi sur le produit">
                                ⚠ rabais % impossible (pas de prix B2B)
                              </span>
                            </>
                          )}
                          <span>·</span>
                          <span>
                            Public conseillé : <span className="text-[#1F4E3D] font-semibold">{recommended != null ? Number(recommended).toFixed(2) : "—"} CHF</span>
                          </span>
                          {marginChf != null && marginChf > 0 && (
                            <>
                              <span>·</span>
                              <span className="text-[#5a7a4f]" title="Marge potentielle pour le revendeur (public conseillé − prix B2B)">
                                marge revendeur {marginChf.toFixed(2)} CHF
                              </span>
                            </>
                          )}
                          {/* Phase 98ag — Marge fabricant nette (interne) */}
                          {fabricantMarginChf != null && (
                            <>
                              <span>·</span>
                              <span
                                className={soldAtLoss
                                  ? "text-[#C05C47] font-semibold"
                                  : fabricantMarginPct != null && fabricantMarginPct < 15
                                    ? "text-[#8C6B2F] font-semibold"
                                    : "text-[#1F4E3D] font-semibold"
                                }
                                title="Marge fabricant nette (prix appliqué − revient). Négative = vente à perte."
                                data-testid={`fabricant-margin-${p.sku}`}
                              >
                                {soldAtLoss ? "⛔ perte " : "✔ marge fab. "}
                                {fabricantMarginChf.toFixed(2)} CHF
                                {fabricantMarginPct != null && ` (${fabricantMarginPct.toFixed(1)}%)`}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      {showOverride && (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.05"
                            min="0"
                            value={currentOverride ?? ""}
                            onChange={(e) => setOverride(p.id, e.target.value)}
                            placeholder={fallbackPrice != null ? fallbackPrice.toFixed(2) : "—"}
                            className={`w-24 h-7 text-xs ${hasOverride ? "border-[#8C6B2F] font-semibold text-[#3D2914] bg-white" : ""}`}
                            data-testid={`override-${p.sku}`}
                            title={hasOverride
                              ? `Prix négocié actif. Effacez ou cliquez sur ✕ pour revenir au tarif standard (${fallbackPrice != null ? fallbackPrice.toFixed(2) + ' CHF' : 'prix B2B brut'}).`
                              : `Saisissez un prix négocié pour ce produit (sinon le tarif standard s'applique : ${fallbackPrice != null ? fallbackPrice.toFixed(2) + ' CHF' : 'prix B2B brut'}).`}
                          />
                          <span className="text-[10px] text-[#A3A6A3]">CHF</span>
                          {hasOverride && (
                            <button
                              type="button"
                              onClick={() => setOverride(p.id, "")}
                              className="text-[#C05C47] hover:text-[#8B3A2A] w-5 h-5 rounded hover:bg-[#FBEEEA] flex items-center justify-center"
                              title="Supprimer ce prix négocié — revient au tarif standard (rabais global ou prix B2B brut)"
                              data-testid={`override-clear-${p.sku}`}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {productsForSelection.length === 0 && <p className="text-center text-xs text-[#A3A6A3] italic py-4">Aucun plat ne correspond.</p>}
              </div>
              <p className="text-[11px] text-[#A3A6A3] mt-2">
                Astuce : laissez les cases <strong>décochées</strong> pour proposer tous les plats au revendeur. Saisissez un prix personnalisé uniquement quand vous voulez déroger au tarif revendeur de base (= prix de cession B2B configuré sur la fiche produit).
              </p>
            </section>

            {/* Notes commerciales */}
            <section>
              <Label>Notes / Conditions commerciales spécifiques</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Conditions de paiement, remise volumétrique, délais de livraison, contact urgent, etc."
                rows={4}
                className="text-sm"
              />
              <p className="text-[11px] text-[#A3A6A3] mt-1">Si non vide, ce texte remplace le bloc « Conditions commerciales » sur le PDF nominatif.</p>
            </section>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-[#E5E5E0] flex justify-between gap-2">
            <div className="text-xs text-[#A3A6A3]">{editing ? "Modification" : "Nouveau revendeur"}</div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-reseller-btn">
                {editing ? "Enregistrer" : "Créer"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
